import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {
  AssignmentGroup,
  AssignmentGroupValueTypeByLayer,
  PlateMap,
  PlateMapSize,
  PlateMapDisplayLayerAttribute,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SchemaFieldDisplayLayer,
  SchematizedAttribute,
} from 'samples/plate-maps/PlateMapConstants';
import {
  applySchemaTagEditToSelected,
  getFlattenedWellIndex,
  getInitialMinimalTag,
  getValueUrlId,
} from 'samples/plate-maps/PlateMapUtil';
import {
  BasePlateMapError,
  FieldValuePlateMapError,
  PlateMapAnnotationError,
  PlateMapAnnotationErrorWithUUID,
  PlateMapErrorLevel,
  PlateMapErrorType,
  PlateMapValidation,
  PlateMapValidationResponse,
  ValidationErrorsByLayerAndLevel,
  ValidationErrorsByLevel,
  WellPlateMapError,
} from 'samples/plate-maps/validation/ValidationTypes';
import {relevantKeyFromFieldType} from 'schemas/tags/TagUtil';
import {immutableRemove} from 'util/ImmutableListUtil';

/**
 * uuid.v4 is not patchable, so it is wrapped in an object that tests can stub.
 * @package
 */
export const uuidGenerator = {
  getUuid: (): string => uuidv4(),
};

export function getFlattenedValidation(
  validation: PlateMapValidation | PlateMapValidationResponse
): Array<PlateMapAnnotationError> {
  return [
    validation.layerValidation,
    validation.wellValidation,
    validation.fieldValueValidation,
    validation.reagentValidation,
  ].flat();
}

/**
 * Group errors by layer key and then by level.
 */
export function getErrorsByLayerAndLevel(
  validationErrors: PlateMapValidation | PlateMapValidationResponse
): ValidationErrorsByLayerAndLevel {
  const allErrors = getFlattenedValidation(validationErrors);

  return allErrors.reduce(
    (errorsGrouped: ValidationErrorsByLayerAndLevel, error: PlateMapAnnotationError) => {
      const layerKey = getAnnotationErrorKey(error);
      errorsGrouped[layerKey] ??= {} as ValidationErrorsByLevel;
      const errors = errorsGrouped[layerKey][error.level] ?? [];
      errors.push({uuid: uuidGenerator.getUuid(), error});
      errorsGrouped[layerKey][error.level] = errors;
      return errorsGrouped;
    },
    {} as ValidationErrorsByLayerAndLevel
  );
}

/**
 * Get a key used for grouping errors by layer for the given display layer attribute.
 */
export function getErrorKeyForDisplayLayerAttribute(attribute: PlateMapDisplayLayerAttribute): string {
  return typeof attribute === 'string' ? attribute : attribute.key;
}

/**
 * Get a key used for grouping errors by layer for the given error.
 */
export function getAnnotationErrorKey(error: PlateMapAnnotationError): string {
  return error.type === PlateMapErrorType.REAGENT ? REAGENTS_ATTRIBUTE_SENTINEL : error.layerId;
}

/**
 * Returns a plate map with all the invalid well and field value errors cleared for the given attribute.
 * For every well plate map error, clear all values for the given field on the errored well.
 * For every field value plate map error, remove only the single invalid value from the errored well.
 */
export function clearSchemaFieldErrors<T extends SchemaFieldDisplayLayer>({
  plateMap,
  selectedAttribute,
  assignmentGroupsById,
  wellErrors,
  fieldValueErrors,
}: {
  plateMap: PlateMap;
  selectedAttribute: SchematizedAttribute;
  assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValueTypeByLayer[T]>};
  wellErrors: Array<WellPlateMapError>;
  fieldValueErrors: Array<FieldValuePlateMapError>;
}): PlateMap {
  const clearedValue = getInitialMinimalTag(selectedAttribute.schemaField);
  const wellErrorIndices = wellErrors.map(({coordinates}) =>
    getFlattenedWellIndex(coordinates.row, coordinates.column, plateMap.size)
  );
  const withWellErrorsCleared = applySchemaTagEditToSelected(
    plateMap,
    selectedAttribute,
    clearedValue,
    new Set(wellErrorIndices)
  );
  const withFieldValueErrorsCleared = produce(withWellErrorsCleared, (draft) => {
    for (const {value, coordinates} of fieldValueErrors) {
      const wellIndex = getFlattenedWellIndex(coordinates.row, coordinates.column, plateMap.size);
      const valueKey: keyof MinimalSchemaTag = relevantKeyFromFieldType(
        selectedAttribute.schemaField.fieldType
      )!;
      const schemaTag: MinimalSchemaTag = draft.wells[wellIndex].schemaTags[selectedAttribute.key];
      let newValue = clearedValue[valueKey] as Array<string>;
      let stringyValue = clearedValue.value;
      const currentValue = schemaTag[valueKey] as Array<string>;
      if (Array.isArray(currentValue)) {
        newValue = immutableRemove<string>(currentValue, value);
        const stringyValues = newValue.map((id) => assignmentGroupsById[id].name);
        stringyValue = stringyValues.join(', ');
      }
      draft.wells[wellIndex].schemaTags[selectedAttribute.key] = {
        ...schemaTag,
        url: null,
        value: stringyValue,
        [valueKey]: newValue,
      };
    }
  });
  return withFieldValueErrorsCleared;
}

export function mergeGroupedValidation(
  left: ValidationErrorsByLayerAndLevel,
  right: ValidationErrorsByLayerAndLevel
): ValidationErrorsByLayerAndLevel {
  const mergedValidation: ValidationErrorsByLayerAndLevel = {};
  for (const attributeKey of Object.keys(left)) {
    mergedValidation[attributeKey] ??= {} as ValidationErrorsByLevel;
    for (const level of Object.keys(left[attributeKey]) as Array<PlateMapErrorLevel>) {
      mergedValidation[attributeKey][level] ??= [];
      mergedValidation[attributeKey][level].push(...left[attributeKey][level]!);
    }
  }
  for (const attributeKey of Object.keys(right)) {
    mergedValidation[attributeKey] ??= {} as ValidationErrorsByLevel;
    for (const level of Object.keys(right[attributeKey]) as Array<PlateMapErrorLevel>) {
      mergedValidation[attributeKey][level] ??= [];
      mergedValidation[attributeKey][level].push(...right[attributeKey][level]!);
    }
  }
  return mergedValidation;
}

export function filterValidation(
  validation: PlateMapValidation,
  predicate: <T extends PlateMapErrorType>(error: BasePlateMapError<T>) => boolean
): PlateMapValidation {
  return {
    layerValidation: validation.layerValidation.filter((error) => predicate(error)),
    wellValidation: validation.wellValidation.filter((error) => predicate(error)),
    fieldValueValidation: validation.fieldValueValidation.filter((error) => predicate(error)),
    reagentValidation: validation.reagentValidation.filter((error) => predicate(error)),
  };
}

export function filterGroupedValidation(
  groupedValidation: ValidationErrorsByLayerAndLevel,
  predicate: (errorWithUuid: PlateMapAnnotationErrorWithUUID) => boolean
): ValidationErrorsByLayerAndLevel {
  const newGroupedValidation: ValidationErrorsByLayerAndLevel = {};
  for (const [layerId, layer] of Object.entries(groupedValidation)) {
    for (const [level, errors] of Object.entries(layer)) {
      const filteredErrors = errors.filter((errorWithUuid) => predicate(errorWithUuid));
      if (filteredErrors.length > 0) {
        newGroupedValidation[layerId] ??= {};
        newGroupedValidation[layerId][level as PlateMapErrorLevel] ??= [];
        newGroupedValidation[layerId][level as PlateMapErrorLevel]!.push(...filteredErrors);
      }
    }
  }
  return newGroupedValidation;
}

/**
 * For the given errors, get the set of flattened well indices that correspond to those well-specific errors.
 */
export function getWellIndicesWithErrors(
  errors: Array<PlateMapAnnotationErrorWithUUID>,
  plateMapSize: PlateMapSize
): Set<number> {
  const wellIndices: Set<number> = new Set();
  for (const {error} of errors) {
    if (error.type === PlateMapErrorType.WELL || error.type === PlateMapErrorType.FIELD_VALUE) {
      wellIndices.add(getFlattenedWellIndex(error.coordinates.row, error.coordinates.column, plateMapSize));
    }
  }
  return wellIndices;
}

/**
 * Get the set of reagent IDs that have reagent errors from the given list.
 */
export function getInvalidIdsFromReagentErrors(errors: Array<PlateMapAnnotationError>): Set<string> {
  const invalidSourceIds = errors.map((error) =>
    error.type === PlateMapErrorType.REAGENT ? error.reagentId : null
  );
  return new Set(invalidSourceIds.filter((id): id is string => id != null));
}

/**
 * Get the set of reagent IDs that have reagent errors from the given list.
 */
export function getInvalidFieldValuesFromErrors(errors: Array<PlateMapAnnotationErrorWithUUID>): Set<string> {
  const invalidValues = errors.map(({error}) =>
    error.type === PlateMapErrorType.FIELD_VALUE ? getValueUrlId(error.value, error.url) : null
  );
  return new Set(invalidValues.filter((value): value is string => value != null));
}

/**
 * For the given groups of validation error objects, check whether any of them are blocking (i.e. ERROR tier)
 */
export function groupedValidationHasAnyBlockingErrors(
  groupedValidation: ValidationErrorsByLayerAndLevel
): boolean {
  const filteredValidation = filterGroupedValidation(
    groupedValidation,
    (errorWithUuid) => errorWithUuid.error.level === PlateMapErrorLevel.ERROR
  );
  return Object.keys(filteredValidation).length > 0;
}
